import { useState } from 'react';
import TicketModal from './TicketModal';
import TicketDetailsModal from './TicketDetailsModal';
import { Ticket } from '@/lib/constants';


interface TicketsTableProps {
    tickets: Ticket[];
    onAssigneeChange?: (ticketId: string, newAssignee: string) => void;
}

export default function TicketsTable({ tickets, onAssigneeChange }: TicketsTableProps) {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [editTicket, setEditTicket] = useState<Ticket | null>(null);

    const [currentPage, setCurrentPage] = useState(1); // starts from 1
    const itemsPerPage = 5; // you can change this number

    const totalPages = Math.ceil(tickets.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentTickets = tickets.slice(startIndex, endIndex);
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

    const handleEdit = (ticket: Ticket) => {
        setEditTicket(ticket);
        setIsCreateModalOpen(true);
        setSelectedTicket(null); 
    };

    const handleAssigneeChange = (ticketId: string, newAssignee: string) => {
        if (onAssigneeChange) {
            onAssigneeChange(ticketId, newAssignee);
        }
    };

    return (
        <div className="pb-3">
        {/* Table Header */}
        <div className="sticky top-[100px] bg-black  pt-4">
            <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
                <table className="w-full table-fixed">
                    <thead>
                        <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
                            {["Ticket ID", "Status", "Organization", "Customer", "Ticket name", "Description", "Added on", "Assigned to", "Last updated"].map((header, index) => (
                                <th key={index} className="text-left p-4 bg-[#1A1A1A] w-[11%]">{header}</th>
                            ))}
                        </tr>
                    </thead>
                </table>
            </div>
        </div>
    
        {/* Table Body */}
        <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
            <table className="w-full table-fixed">
                <tbody>
                    {currentTickets.map((ticket) => (
                        <tr key={ticket.id}  onClick={() => handleEdit(ticket)} className="border-b border-white/[0.08] hover:bg-[#3a3a3a33] cursor-pointer">
                            <td className="p-4 text-gray-400 w-[11%]">{ticket.id}</td>
                            <td className="p-4 w-[11%]">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium
                                    ${ticket.status === 'Open' ? 'bg-[#1C3829] text-[#4ADE80]' :
                                    ticket.status === 'Assigned' ? 'bg-[#1E3A8A] text-[#60A5FA]' :
                                    ticket.status === 'Inprogress' ? 'bg-[#92400E] text-[#FBBF24]' :
                                    ticket.status === 'Resolved' ? 'bg-[#065F46] text-[#34D399]' :
                                    ticket.status === 'Closed' ? 'bg-[#7F1D1D] text-[#F87171]' :
                                    'bg-[#374151] text-[#9CA3AF]'}`} >
                                    {ticket.status}
                                </span>
                            </td>
                            <td className="p-4 text-gray-400 w-[11%]">{ticket.organization}</td>
                            <td className="p-4 text-gray-400 w-[11%]">{ticket.customer}</td>
                            <td className="p-4 text-white w-[11%]">{ticket.name}</td>
                            <td className="p-4 text-gray-400 max-w-[300px] w-[11%]">
                                <div className="truncate whitespace-normal overflow-hidden text-sm leading-5" 
                                     style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                    {ticket.description}
                                </div>
                            </td>
                            <td className="p-4 text-gray-400 w-[11%]">{ticket.addedOn}</td>
                            <td className="p-4 w-[11%]">
                                <select 
                                    className="bg-[#2A2A2A] border border-white/[0.08] rounded px-2 py-1 text-gray-400"
                                    onClick={(e) => e.stopPropagation()}
                                    value={ticket.assignedTo || ''}
                                    onChange={(e) => handleAssigneeChange(ticket.id, e.target.value)}
                                >
                                    {ticket?.assignees && ticket?.assignees?.map((assignee) => (
                                        <option key={assignee.id} value={assignee.id}>
                                            {assignee.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td className="p-4 text-gray-400 w-[11%]">{ticket.lastUpdated}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        {/* Pagination */}
 <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
                    <div>
                        Showing {startIndex + 1}-{Math.min(endIndex, tickets.length)} from {tickets.length}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="p-2 hover:text-white disabled:opacity-50"
                        >
                            ←
                        </button>
                        {pages.map(page => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`w-8 h-8 rounded flex items-center justify-center ${currentPage === page
                                        ? 'bg-[#3A3A3A] text-white'
                                        : 'hover:text-white'
                                    }`}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="p-2 hover:text-white disabled:opacity-50"
                        >
                            →
                        </button>
                    </div>
                </div>
                

        {/* Add TicketModal for editing */}
        <TicketModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            initialData={editTicket}
            mode="edit"
        />
    </div>
    
    );
} 